"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface CircularProgressProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The progress value as a percentage (0-100).
   */
  progress: number;

  /**
   * The diameter of the circle in pixels.
   * @default 24
   */
  size?: number;

  /**
   * The width of the progress stroke in pixels.
   * @default 2
   */
  strokeWidth?: number;

  /**
   * Background color for the track (unfilled portion)
   * @default "hsl(var(--muted))"
   */
  trackColor?: string;

  /**
   * Progress color (filled portion)
   * @default "hsl(var(--primary))"
   */
  progressColor?: string;
}

export const CircularProgress = React.forwardRef<
  HTMLDivElement,
  CircularProgressProps
>(
  (
    {
      progress,
      size = 24,
      strokeWidth = 2,
      trackColor = "hsl(var(--muted))",
      progressColor = "hsl(var(--primary))",
      className,
      ...props
    },
    ref
  ) => {
    const center = size / 2;
    const radius = center - strokeWidth;
    const circumference = 2 * Math.PI * radius;

    // Ensure progress is between 0 and 100
    const normalizedProgress = Math.min(Math.max(progress, 0), 100);

    // Calculate the offset for the stroke-dashoffset
    // Start from top (rotate -90deg) and fill clockwise
    const offset = circumference - (normalizedProgress / 100) * circumference;

    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center justify-center", className)}
        {...props}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
        >
          {/* Background circle (track) */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={trackColor}
            strokeWidth={strokeWidth}
          />

          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-300 ease-in-out"
          />
        </svg>
      </div>
    );
  }
);

CircularProgress.displayName = "CircularProgress";
